import { useMemo } from 'react';
import { noop } from 'lodash';

import { PatientGroup, PatientGroupQuery } from '@dreryk/egabinet-patient-frontend-api';

import {
   createDynamicColumnsWidth,
   EditIcon,
   HexColorDot,
   HexColorDotSize,
   Link,
   RemoveIcon,
   StyledFlex,
   TableColumnConfiguration,
} from '@dreryk/react-components';
import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { Slot } from '@dreryk/watermelon';

import { translationKeys } from '../../translations';

import { CustomPatientGroupsUpdateModalOpener } from './update/CustomPatientGroupsUpdateModal.opener';
import { CustomPatientGroupsRemoveModalOpener } from './remove/CustomPatientGroupsRemoveModal.opener';

export const useCustomPatientGroupsTableDataConfiguration = (): LazyLoadedDataConfigurationByCqrsQuery<PatientGroup> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<PatientGroup>(() => {
      const query = new PatientGroupQuery();
      query.predefined = Slot.of(false);
      return query;
   });
};

export const useCustomPatientGroupsTableColumnsConfiguration = (): TableColumnConfiguration<PatientGroup>[] => {
   return useMemo<TableColumnConfiguration<PatientGroup>[]>(() => {
      const columnsWidth = createDynamicColumnsWidth({
         color: '64px',
         name: '100%',
         options: '64px',
      });

      return [
         {
            label: translationKeys.color,
            name: 'color',
            width: columnsWidth.color,
            sortingDisabled: true,
            accessor: 'primaryColor',
            renderer: ({ bean: { primaryColor, darkModeColor, id } }) => (
               <HexColorDot
                  testId={id}
                  color={primaryColor.asHexString()}
                  darkModeColor={darkModeColor?.asHexString()}
                  size={HexColorDotSize.SMALL}
               />
            ),
         },
         {
            label: translationKeys.name,
            name: 'name',
            width: columnsWidth.name,
            sortingDisabled: true,
            accessor: 'name',
         },
         {
            name: 'options',
            width: columnsWidth.options,
            sortingDisabled: true,
            accessor: noop,
            renderer: ({ bean: { id, name } }) => (
               <StyledFlex>
                  <CustomPatientGroupsUpdateModalOpener patientGroupId={id}>
                     {({ openModal }) => (
                        <Link testId={'customPatientGroupsUpdateLink'} icon={<EditIcon />} onClick={openModal} />
                     )}
                  </CustomPatientGroupsUpdateModalOpener>

                  <CustomPatientGroupsRemoveModalOpener patientGroupId={id} patientGroupName={name}>
                     {({ openModal }) => (
                        <Link testId={'customPatientGroupsDeleteLink'} icon={<RemoveIcon />} onClick={openModal} />
                     )}
                  </CustomPatientGroupsRemoveModalOpener>
               </StyledFlex>
            ),
         },
      ];
   }, []);
};
